import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../lib/currency';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DateFilter } from '../components/DateFilter';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Plus, 
  MoreVertical, 
  Trash2, 
  Edit2, 
  MapPin, 
  Calendar, 
  Clock, 
  Users,
  Loader2,
  Plane,
  Megaphone,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const tripStatuses = [
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-700' },
  { value: 'inactive', label: 'Inactive', color: 'bg-gray-100 text-gray-700' },
  { value: 'completed', label: 'Completed', color: 'bg-blue-100 text-blue-700' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700' },
];

const getStatusBadge = (status) => {
  const statusConfig = tripStatuses.find(s => s.value === status);
  return statusConfig ? (
    <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
  ) : (
    <Badge>{status}</Badge>
  );
};

export const Trips = () => {
  const [activeTab, setActiveTab] = useState('upcoming');
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [dateFilter, setDateFilter] = useState({ startDate: '', endDate: '' });
  
  const [form, setForm] = useState({
    trip_name: '',
    destination: '',
    trip_date: '',
    days: 1,
    cost_per_person: '',
    trip_type: 'upcoming',
    status: 'active',
    description: '',
    inclusions: '',
    max_participants: 0,
  });

  useEffect(() => {
    loadTrips();
  }, [activeTab, dateFilter]);

  const loadTrips = async () => {
    setLoading(true);
    try {
      const params = { type: activeTab };
      if (dateFilter.startDate) params.start_date = dateFilter.startDate;
      if (dateFilter.endDate) params.end_date = dateFilter.endDate;
      
      const { data } = await api.get('/trips', { params });
      setTrips(data);
    } catch (error) {
      toast.error('Failed to load trips');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        cost_per_person: parseFloat(form.cost_per_person),
        days: parseInt(form.days),
        max_participants: parseInt(form.max_participants) || 0,
      };
      
      if (editingTrip) {
        await api.put(`/trips/${editingTrip.id}`, payload);
        toast.success('Trip updated successfully');
      } else {
        await api.post('/trips', payload);
        toast.success('Trip created successfully');
      }
      
      setDialogOpen(false);
      resetForm();
      loadTrips();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save trip');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (trip) => {
    setEditingTrip(trip);
    setForm({
      trip_name: trip.trip_name,
      destination: trip.destination,
      trip_date: trip.trip_date,
      days: trip.days,
      cost_per_person: trip.cost_per_person,
      trip_type: trip.trip_type,
      status: trip.status,
      description: trip.description || '',
      inclusions: trip.inclusions || '',
      max_participants: trip.max_participants || 0,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this trip?')) return;
    try {
      await api.delete(`/trips/${id}`);
      toast.success('Trip deleted successfully');
      loadTrips();
    } catch (error) {
      toast.error('Failed to delete trip');
    }
  };

  const resetForm = () => {
    setEditingTrip(null);
    setForm({
      trip_name: '',
      destination: '',
      trip_date: '',
      days: 1,
      cost_per_person: '',
      trip_type: activeTab,
      status: 'active',
      description: '',
      inclusions: '',
      max_participants: 0,
    });
  };

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div data-testid="trips-container" className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight" style={{ color: '#84bd0e' }}>
            Trips Management
          </h1>
          <p className="text-muted-foreground mt-1">Manage upcoming and announced trips</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="text-white" style={{ backgroundColor: '#383838' }}>
              <Plus className="w-4 h-4 mr-2" />
              Add Trip
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-outfit">
                {editingTrip ? 'Edit Trip' : 'Create New Trip'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label>Trip Name *</Label>
                  <Input
                    value={form.trip_name}
                    onChange={(e) => setForm({ ...form, trip_name: e.target.value })}
                    placeholder="e.g., Goa Beach Adventure"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Destination *</Label>
                  <Input
                    value={form.destination}
                    onChange={(e) => setForm({ ...form, destination: e.target.value })}
                    placeholder="e.g., Goa"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Trip Date *</Label>
                  <Input
                    type="date"
                    value={form.trip_date}
                    onChange={(e) => setForm({ ...form, trip_date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Duration (Days) *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={form.days}
                    onChange={(e) => setForm({ ...form, days: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cost per Person (₹) *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.cost_per_person}
                    onChange={(e) => setForm({ ...form, cost_per_person: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Trip Type *</Label>
                  <Select value={form.trip_type} onValueChange={(v) => setForm({ ...form, trip_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="upcoming">Upcoming</SelectItem>
                      <SelectItem value="announced">Announced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tripStatuses.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Max Participants</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.max_participants}
                    onChange={(e) => setForm({ ...form, max_participants: e.target.value })}
                    placeholder="0 = unlimited"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={2}
                    placeholder="Brief description of the trip..."
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Inclusions</Label>
                  <Textarea
                    value={form.inclusions}
                    onChange={(e) => setForm({ ...form, inclusions: e.target.value })}
                    rows={2}
                    placeholder="e.g., Hotel, Breakfast, Transport..."
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="text-white" style={{ backgroundColor: '#383838' }} disabled={submitting}>
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingTrip ? 'Update Trip' : 'Create Trip'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Date Filter */}
      <DateFilter onFilterChange={handleDateFilterChange} />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-100 dark:bg-slate-800">
          <TabsTrigger value="upcoming" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
            <Plane className="w-4 h-4 mr-2" />
            Upcoming Trips
          </TabsTrigger>
          <TabsTrigger value="announced" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
            <Megaphone className="w-4 h-4 mr-2" />
            Announced Trips
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
            </div>
          ) : trips.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <MapPin className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  No {activeTab} trips found
                </h3>
                <p className="text-muted-foreground text-center mb-4">
                  Get started by creating a new {activeTab} trip
                </p>
                <Button onClick={() => setDialogOpen(true)} className="text-white" style={{ backgroundColor: '#383838' }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Trip
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trips.map((trip) => (
                <Card key={trip.id} className="border-border hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          {trip.trip_code && (
                            <Badge variant="outline" className="font-mono text-xs bg-[#84bd0e]/10 text-[#84bd0e] border-[#84bd0e]/30">
                              {trip.trip_code}
                            </Badge>
                          )}
                        </div>
                        <CardTitle className="font-outfit text-lg" style={{ color: '#84bd0e' }}>
                          {trip.trip_name}
                        </CardTitle>
                        <div className="flex items-center gap-1 text-muted-foreground mt-1">
                          <MapPin className="w-4 h-4" />
                          <span className="text-sm">{trip.destination}</span>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(trip)}>
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(trip.id)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          {formatDate(trip.trip_date)}
                        </div>
                        {getStatusBadge(trip.status)}
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="w-4 h-4" />
                          {trip.days} {trip.days === 1 ? 'Day' : 'Days'}
                        </div>
                        {trip.max_participants > 0 && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Users className="w-4 h-4" />
                            {trip.current_participants || 0}/{trip.max_participants}
                          </div>
                        )}
                      </div>
                      <div className="pt-2 border-t border-border">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Cost per person</span>
                          <span className="text-lg font-bold" style={{ color: '#383838' }}>
                            {formatCurrency(trip.cost_per_person)}
                          </span>
                        </div>
                      </div>
                      {trip.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {trip.description}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
